/**
 * B-24b WS-3 — Platform AI Quality cluster (Operator Console version).
 *
 * Wired to:
 *   - GET /admin/sa-dashboard/calibration-health (tenant-zone: per-tenant
 *     calibration pass rate, guardrails drift, calibration coverage counts)
 *   - GET /admin/dashboard/quality (platform-zone: heatmap / down-rate data
 *     from `platform_rating_signals` — the EXISTING endpoint, reused)
 *
 * TWO-ZONE BOUNDARY (IMPERATIVE):
 *   Calibration data is tenant-scoped and CAN name tenants.
 *   The platform pass-rate / down-rate heatmap comes from platform_rating_signals
 *   and is cross-tenant aggregate — it is NEVER joined to or attributed to a
 *   named tenant. The two sub-sections are visually and data-wise separate.
 *
 * Low-data (IV-L1, IV-Z3):
 *   - bias_pass_rate: null (insufficient sample) → "building baseline" text
 *   - drift counts 0 → displayed as 0 (not suppressed)
 *   - heatmap gated on total_ratings_window >= HEATMAP_MIN_SAMPLE; below it a
 *     "building baseline — N of 30 ratings" placeholder, NOT a solid block
 *     (HoD teardown P0-3/P0-6/P2-3)
 */
import ReactECharts from "echarts-for-react";
import { useQuery } from "@tanstack/react-query";
import { useSaCalibrationHealth } from "@/hooks/queries/admin/useSaDashboard";
import { fetchQuality, type QualityPanelResponse } from "@/lib/dashboardApi";
import { echartsTheme } from "@/lib/echartsTheme";
import {
  ECHARTS_AXIS_LABEL_COLOR,
  ECHARTS_TOOLTIP_BG,
  ECHARTS_TOOLTIP_BORDER,
  ECHARTS_HEATMAP_START,
  ECHARTS_COLORS,
  ECHARTS_SPLIT_COLOR,
} from "@/lib/tokens";
import { OperatorPanel } from "./OperatorPanel";

/** Minimum platform ratings before the heatmap renders (P0-3/P0-6). Below this
 *  we show the "building baseline" placeholder, NOT a misleading solid block. */
const HEATMAP_MIN_SAMPLE = 30;

/** Map an action_type to a human label — NEVER leak the literal model id
 *  (positioning rule, P2-1). The y-axis series key is "<action>|<model>". */
function humanActionLabel(actionType: string): string {
  switch (actionType) {
    case "rank_one":
      return "Rank one";
    case "rank":
      return "Ranking";
    case "ai_evaluation":
    case "evaluation":
      return "AI evaluation";
    case "screening":
      return "Screening";
    case "summary":
      return "Summary";
    default:
      // Title-case the raw action; still never the model name.
      return actionType
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
  }
}

export function OperatorQualityPanel() {
  const calibQ = useSaCalibrationHealth();
  const platformQ = useQuery<QualityPanelResponse, Error>({
    queryKey: ["admin", "dashboard", "quality"],
    queryFn: fetchQuality,
    staleTime: 60_000,
  });

  const calibData = calibQ.data;
  const platformData = platformQ.data;

  // Calibration coverage counts
  const calibrated = calibData
    ? calibData.tenants.filter((t) => t.calibrated).length
    : 0;
  const uncalibrated = calibData
    ? calibData.tenants.filter((t) => !t.calibrated).length
    : 0;
  const drifting = calibData
    ? calibData.tenants.filter((t) => t.drift).length
    : 0;
  const total = calibData ? calibData.tenants.length : 0;

  // Platform heatmap (zero-FK, cross-tenant aggregate — platform zone).
  // Gate on sample count (P0-3/P0-6): below HEATMAP_MIN_SAMPLE we render the
  // placeholder, NOT a solid block that reads as a real (mint) signal.
  const totalRatings = platformData?.kpis.total_ratings_window ?? 0;
  const heatmapReady =
    !!platformData &&
    platformData.cells.length > 0 &&
    totalRatings >= HEATMAP_MIN_SAMPLE;
  let heatmapOption: Record<string, unknown> | null = null;
  if (heatmapReady && platformData) {
    const weeks = Array.from(
      new Set(platformData.cells.map((c) => c.week_start)),
    ).sort();
    // Underlying series KEY (action|model) drives data indexing; the displayed
    // y-axis label is the HUMAN action label only — never the model id (P2-1).
    const seriesKeys = Array.from(
      new Set(
        platformData.cells.map((c) => `${c.action_type}|${c.model_used}`),
      ),
    );
    const seriesLabels = seriesKeys.map((k) =>
      humanActionLabel(k.split("|")[0] ?? k),
    );
    const heatData = platformData.cells.map((c) => [
      weeks.indexOf(c.week_start),
      seriesKeys.indexOf(`${c.action_type}|${c.model_used}`),
      c.down_rate ?? -1,
    ]);
    heatmapOption = {
      ...echartsTheme,
      grid: { left: 80, right: 10, top: 10, bottom: 30 },
      xAxis: {
        type: "category",
        data: weeks,
        axisLabel: { color: ECHARTS_AXIS_LABEL_COLOR, fontSize: 9, rotate: 30 },
        axisLine: { lineStyle: { color: ECHARTS_TOOLTIP_BORDER } },
        axisTick: { show: false },
      },
      yAxis: {
        type: "category",
        data: seriesLabels,
        axisLabel: { color: ECHARTS_AXIS_LABEL_COLOR, fontSize: 9 },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      visualMap: {
        min: 0,
        max: 0.2,
        calculable: false,
        show: false,
        inRange: {
          color: [ECHARTS_HEATMAP_START, ECHARTS_COLORS[3], ECHARTS_COLORS[4]],
        },
      },
      series: [{ type: "heatmap", data: heatData }],
      tooltip: {
        position: "top",
        backgroundColor: ECHARTS_TOOLTIP_BG,
        borderColor: ECHARTS_TOOLTIP_BORDER,
        textStyle: { color: ECHARTS_SPLIT_COLOR, fontSize: 11 },
        formatter: (params: { value: number[] }) => {
          const v = params.value[2];
          return v !== undefined && v >= 0
            ? `${(v * 100).toFixed(1)}% down`
            : "n/a";
        },
      },
    };
  }

  return (
    <OperatorPanel
      testId="operator-quality-panel"
      kicker="AI quality — platform"
      title="Platform AI quality"
    >
      <div className="space-y-4">
        {/* Calibration coverage (tenant-zone, counts only — no per-tenant name here) */}
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-text-tertiary">
            Calibration coverage
          </p>
          {calibQ.isLoading ? (
            <div className="mt-2 h-10 animate-pulse rounded bg-surface-canvas" />
          ) : (
            <div className="mt-2 grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="num-kpi font-mono text-xl font-bold text-brand-positive">
                  {calibrated}
                </div>
                <div className="text-[10px] uppercase tracking-wide text-text-tertiary">
                  calibrated
                </div>
              </div>
              <div>
                <div
                  className={
                    "num-kpi font-mono text-xl font-bold " +
                    (uncalibrated > 0 ? "text-brand-warn" : "text-text-secondary")
                  }
                >
                  {uncalibrated}
                </div>
                <div className="text-[10px] uppercase tracking-wide text-text-tertiary">
                  uncalibrated
                </div>
              </div>
              <div>
                <div
                  className={
                    "num-kpi font-mono text-xl font-bold " +
                    (drifting > 0 ? "text-brand-warn" : "text-text-secondary")
                  }
                >
                  {drifting}
                </div>
                <div className="text-[10px] uppercase tracking-wide text-text-tertiary">
                  guardrails drift
                </div>
              </div>
            </div>
          )}
          {/* Per-tenant calibration pass rates (tenant-zone, nameable) */}
          {calibData && total > 0 && (
            <div className="mt-2 max-h-[120px] overflow-y-auto">
              {calibData.tenants.map((t) => (
                <div
                  key={t.tenant_id}
                  className="grid grid-cols-[1fr_72px_56px] items-center gap-2 py-1 border-b border-border-default last:border-0"
                >
                  <span className="truncate text-[11px] text-text-secondary">
                    {t.name}
                  </span>
                  <span
                    className={
                      "text-right font-mono text-[11px] tabular-nums font-semibold " +
                      (t.bias_pass_rate === null
                        ? "text-text-tertiary italic"
                        : t.bias_pass_rate >= 98
                          ? "text-brand-positive"
                          : t.bias_pass_rate >= 94
                            ? "text-brand-warn"
                            : "text-brand-negative")
                    }
                  >
                    {t.bias_pass_rate === null
                      ? "building"
                      : `${t.bias_pass_rate.toFixed(1)}%`}
                  </span>
                  <span
                    className={
                      "text-right text-[10px] " +
                      (t.drift ? "text-brand-warn font-semibold" : "text-text-tertiary")
                    }
                  >
                    {t.drift ? t.guardrails_version + " ↑" : t.guardrails_version}
                  </span>
                </div>
              ))}
            </div>
          )}
          {calibData && total === 0 && (
            <p
              data-testid="quality-panel-no-calibration"
              className="mt-2 text-xs text-text-tertiary"
            >
              No calibration data yet — will appear after first tenant run.
            </p>
          )}
        </div>

        {/* Platform down-rate heatmap (platform-zone — cross-tenant aggregate, NOT nameable) */}
        <div className="border-t border-border-default pt-3">
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-text-tertiary">
            Down-rate heatmap — platform aggregate · not tenant-attributable
          </p>
          {platformQ.isLoading ? (
            <div className="mt-2 h-28 animate-pulse rounded bg-surface-canvas" />
          ) : heatmapReady && heatmapOption ? (
            <ReactECharts
              style={{ height: 140, marginTop: 8 }}
              option={heatmapOption}
              notMerge
            />
          ) : (
            // Below the sample floor (or no cells yet) → placeholder, never a
            // solid block. Fold the raw ratings count INTO the copy (P2-3).
            <p
              data-testid="quality-panel-no-ratings"
              className="mt-2 text-xs text-text-tertiary"
            >
              Building baseline — {totalRatings} of {HEATMAP_MIN_SAMPLE} ratings.
              Heatmap appears once enough platform ratings are collected.
            </p>
          )}
        </div>
      </div>
    </OperatorPanel>
  );
}
